import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { DateRangeType } from "react-tailwindcss-datepicker";
import { useTranslation } from "react-i18next";

import PerformanceUserActivityTable from "../../components/shared/Performance/PerformanceUserActivityTable";
import PerformanceUserActivityGraph from "../../components/shared/Performance/PerformanceUserActivityGraph";
import { ActivityMetricKey, DEFAULT_ACTIVITY_METRIC } from "../../components/shared/Performance/activityMetrics";
import StationOverTargetGraph from "../../components/shared/Performance/StationOverTargetGraph";
import usePerformance from "../../data/usePerformance";
import useStationOverTarget from "../../data/useStationOverTarget";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import toIsoDate from "../../utils/toIsoDate";

export default function Performance() {
  const { t } = useTranslation("reports");
  const { setInfo } = usePageNameContext();

  const now = DateTime.now();
  const [dates, setDates] = useState<DateRangeType>({
    startDate: now.startOf("month").toJSDate(),
    endDate: now.toJSDate(),
  });
  const [selectedMetric, setSelectedMetric] = useState<ActivityMetricKey>(DEFAULT_ACTIVITY_METRIC);

  useEffect(() => {
    setInfo({
      name: t("Performance"),
      desc: t("Personnel activity and performance metrics"),
    });
  }, [setInfo, t]);

  const from = useMemo(() => toIsoDate(dates.startDate), [dates.startDate]);
  const to = useMemo(() => toIsoDate(dates.endDate), [dates.endDate]);

  const { data, isLoading } = usePerformance(from, to);

  // Time over target is a Production Manager figure: it comes off the assembly timers that only
  // exist with that feature, so the graph (and its request) stay off without the flag.
  const hasProductionManager = useFeatureFlag("Production Manager");
  const overTarget = useStationOverTarget(from, to, hasProductionManager);

  // Inclusive day count for the selected range, used to work out daily averages.
  const daysInPeriod = useMemo(() => {
    if (!from || !to) return 1;
    const start = DateTime.fromISO(from);
    const end = DateTime.fromISO(to);
    return Math.max(1, Math.floor(end.diff(start, "days").days) + 1);
  }, [from, to]);

  const stats = data?.data;
  // Only show the loading state on first load (matches the Dashboard); focus refetches update in place.
  const isLoadingStats = isLoading || !stats;

  return (
    <div className="mt-4 grid grid-cols-1 gap-4">
      <PerformanceUserActivityTable
        title={t("Personnel Activity")}
        rowLabelColumn={t("User")}
        stats={stats}
        daysInPeriod={daysInPeriod}
        isLoading={isLoadingStats}
        dates={dates}
        onDatesChange={setDates}
        selectedMetric={selectedMetric}
        onMetricSelect={setSelectedMetric}
      />
      <PerformanceUserActivityGraph
        title={t("Activity by User")}
        stats={stats}
        isLoading={isLoadingStats}
        selectedMetric={selectedMetric}
        onMetricChange={setSelectedMetric}
      />
      {hasProductionManager && (
        // isLoading && isFetching: a disabled query (cleared range) sits at isLoading forever in
        // react-query v4, and a background refetch should update in place rather than spin.
        <StationOverTargetGraph
          title={t("Time Over Target by Station")}
          result={overTarget.data?.data}
          isLoading={overTarget.isLoading && overTarget.isFetching}
          isError={overTarget.isError}
          hasRange={!!from && !!to}
        />
      )}
    </div>
  );
}
